import { ChangeEvent, JSX } from 'react';
import {
    Box,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useGetList } from 'react-admin';
import { AdminMiniDescriptor, AdminMiniSenderMode } from '../admin-mini.types';
import { NokPersona, NokWebsite } from '../../../../../../types/entities';

export interface AdminMiniSender {
    senderMode: AdminMiniSenderMode;
    nokChoice: 'random' | 'nok';
    fromNok: string;
    fromChoice: 'random' | 'persona';
    fromPersona: string;
    fromName: string;
    fromEmail: string;
}

export interface AdminMiniSenderPanelProps {
    tool: AdminMiniDescriptor;
    value: AdminMiniSender;
    onChange: (value: AdminMiniSender) => void;
    disabled?: boolean;
}

const MODE_LABELS: Record<AdminMiniSenderMode, { title: string, detail: string }> = {
    nok: {
        title: 'As a Nok persona',
        detail: 'Sends from a persona on that Nok website\'s own domain, and replies come back to them.',
    },
    compliance: {
        title: 'From a person here',
        detail: 'Sends from the address you type, over the system compliance domain.',
    },
};

/**
 * Who a run introduces itself as.
 *
 * **A tool offering one mode shows no choice at all.** Mystery Shopper has only ever had the Nok
 * pipe, and a radio group with one option is a question that wastes the reader's attention on an
 * answer they cannot change.
 *
 * A sniff offers both, chosen per run (the user's decision of 2026-09-11), and they are genuinely
 * different pipes rather than two addresses: a different domain, a different signature, a different
 * Mailgun endpoint, and only one of them has a persona a reply can be threaded through. That is why
 * the two halves below are mutually exclusive rather than a shared set of fields.
 */
export const AdminMiniSenderPanel: (props: AdminMiniSenderPanelProps) => JSX.Element =
    (props: AdminMiniSenderPanelProps): JSX.Element => {
        const { value, onChange, tool } = props;
        const offersAChoice: boolean = tool.senderModes.length > 1;

        const { data: noks } = useGetList<NokWebsite>('nok_websites', {
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'name', order: 'ASC' },
        });

        // Only the chosen Nok's personas. Asking for all of them and filtering in the browser is
        // what the older form does, and it offers personas that cannot send from the chosen site.
        const { data: personas } = useGetList<NokPersona>('nok_personas', {
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'name', order: 'ASC' },
            filter: value.fromNok === '' ? {} : { 'nokWebsite.id': value.fromNok },
        });

        function set(patch: Partial<AdminMiniSender>): void {
            onChange({ ...value, ...patch });
        }

        return (
            <Stack spacing={ 2 } sx={ { width: '100%' } }>
                <Typography sx={ {
                    fontSize: 'var(--wf-eyebrow)',
                    letterSpacing: 'var(--wf-track)',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontWeight: 700,
                } }>
                    { 'Sends as' }
                </Typography>

                { offersAChoice && (
                    <RadioGroup
                        value={ value.senderMode }
                        onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                            set({ senderMode: e.target.value as AdminMiniSenderMode }) }
                    >
                        { tool.senderModes.map((mode: AdminMiniSenderMode): JSX.Element => (
                            <FormControlLabel
                                key={ mode }
                                value={ mode }
                                disabled={ props.disabled }
                                control={ <Radio size="small" /> }
                                label={
                                    <Box sx={ { paddingBottom: '6px' } }>
                                        <Typography sx={ { fontSize: '14px', fontWeight: 600 } }>
                                            { MODE_LABELS[mode].title }
                                        </Typography>
                                        <Typography sx={ { fontSize: '12px', color: 'text.secondary' } }>
                                            { MODE_LABELS[mode].detail }
                                        </Typography>
                                    </Box>
                                }
                                sx={ { alignItems: 'flex-start', marginLeft: 0 } }
                            />
                        )) }
                    </RadioGroup>
                ) }

                { value.senderMode === 'nok' && (
                    <Stack spacing={ 2 }>
                        <TextField select
                                   size="small"
                                   label="Nok website"
                                   value={ value.nokChoice === 'random' ? 'random' : value.fromNok }
                                   disabled={ props.disabled }
                                   onChange={ (e: ChangeEvent<HTMLInputElement>): void => {
                                       const picked: string = e.target.value;
                                       set(picked === 'random'
                                           // Clearing the persona with the Nok. Leaving it would
                                           // send as a persona that belongs to a different site.
                                           ? { nokChoice: 'random', fromNok: '', fromChoice: 'random', fromPersona: '' }
                                           : { nokChoice: 'nok', fromNok: picked, fromChoice: 'random', fromPersona: '' });
                                   } }>
                            <MenuItem value="random">{ 'Any Nok website' }</MenuItem>
                            { (noks ?? []).map((nok: NokWebsite): JSX.Element => (
                                <MenuItem key={ nok.id } value={ String(nok.id) }>{ nok.name }</MenuItem>
                            )) }
                        </TextField>

                        <TextField select
                                   size="small"
                                   label="Persona"
                                   value={ value.fromChoice === 'random' ? 'random' : value.fromPersona }
                                   disabled={ props.disabled || value.nokChoice === 'random' }
                                   helperText={ value.nokChoice === 'random'
                                       ? 'Pick a Nok website first to choose a persona'
                                       : undefined }
                                   onChange={ (e: ChangeEvent<HTMLInputElement>): void => {
                                       const picked: string = e.target.value;
                                       set(picked === 'random'
                                           ? { fromChoice: 'random', fromPersona: '' }
                                           : { fromChoice: 'persona', fromPersona: picked });
                                   } }>
                            <MenuItem value="random">{ 'Any persona from that site' }</MenuItem>
                            { (personas ?? []).map((persona: NokPersona): JSX.Element => (
                                <MenuItem key={ persona.id } value={ String(persona.id) }>
                                    { `${ persona.name } - ${ persona.email }` }
                                </MenuItem>
                            )) }
                        </TextField>
                    </Stack>
                ) }

                { value.senderMode === 'compliance' && (
                    <Stack spacing={ 2 }>
                        <TextField size="small"
                                   label="Sender name"
                                   placeholder="How this should be signed"
                                   value={ value.fromName }
                                   disabled={ props.disabled }
                                   onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                       set({ fromName: e.target.value }) } />
                        <TextField size="small"
                                   label="Sender address"
                                   placeholder="someone@noknok.ai"
                                   value={ value.fromEmail }
                                   disabled={ props.disabled }
                                   helperText="Replies come back here. The domain it sends over is a system setting."
                                   onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                       set({ fromEmail: e.target.value }) } />
                    </Stack>
                ) }
            </Stack>
        );
    };
